import * as THREE from "three";
import { noiseGLSL } from "./noise.glsl.js";

// Plate horizon + sun measured on the cleaned reference plate (uv, y up)
export const PLATE = { aspect: 1264 / 848, horizon: 0.565, sun: [0.818, 0.595] };
export const OVERSCAN = 1.15;

const frag = /* glsl */ `
precision highp float;
uniform sampler2D uTex;
uniform vec2 uRes;
uniform vec2 uCenter;
uniform vec2 uVis;
uniform vec2 uShift;
uniform float uRoll;
uniform vec2 uVP;
uniform float uHorizon;
uniform float uPhase;
uniform float uFlowK;
uniform vec3 uCamR;
uniform vec3 uCamU;
uniform vec3 uCamF;
uniform vec2 uTan;
uniform vec2 uTravel;
uniform float uMistH;
uniform vec3 uSunW;
uniform vec2 uSunUv;
uniform float uTime;
uniform float uFade;
varying vec2 vUv;
${noiseGLSL}

vec2 toPlate(vec2 s) {
  float asp = uRes.x / uRes.y;
  vec2 c = s - 0.5;
  c.x *= asp;
  float cs = cos(uRoll), sn = sin(uRoll);
  c = mat2(cs, sn, -sn, cs) * c;
  c.x /= asp;
  return c + 0.5 - uShift;
}
vec3 plate(vec2 p) { return texture2D(uTex, uCenter + (p - 0.5) * uVis).rgb; }

void main() {
  vec2 p = toPlate(vUv);
  vec2 vp = toPlate(uVP);
  float hy = uHorizon;
  float f = p.y < hy ? (hy - p.y) / hy : 0.22 * (p.y - hy) / (1.0 - hy);
  vec2 dir = p - vp;
  float dl = length(dir);
  dir *= min(dl, 1.3) / max(dl, 1e-4);
  vec2 off = dir * f * uFlowK;
  float jitter = vnoise(p * 2.3) * 0.6;
  float ph0 = fract(uPhase + jitter);
  float ph1 = fract(uPhase + jitter + 0.5);
  vec3 A = plate(p + off * ph0);
  vec3 B = plate(p + off * ph1);
  float w = 1.0 - abs(2.0 * ph0 - 1.0);
  vec3 col = mix(B, A, w);

  // drifting mist on a world-space layer below the aircraft
  vec2 n = vUv * 2.0 - 1.0;
  vec3 rd = normalize(uCamF + uCamR * n.x * uTan.x + uCamU * n.y * uTan.y);
  if (rd.y < -0.004) {
    float t = uMistH / -rd.y;
    vec2 g = rd.xz * t - uTravel;
    float fade = smoothstep(120.0, 30.0, t) * smoothstep(0.8, 4.0, t);
    vec2 q = g * 0.07;
    float m = fbm(q + vec2(fbm(q * 0.6 + 3.1), fbm(q * 0.6 - 1.7)) * 1.6);
    float mist = smoothstep(0.42, 0.86, m) * fade;
    vec2 h = normalize(rd.xz);
    vec2 sh = normalize(uSunW.xz);
    float sunAmt = pow(max(dot(h, sh), 0.0), 3.0);
    vec3 mc = mix(vec3(0.52, 0.42, 0.44), vec3(1.0, 0.76, 0.52), 0.3 + 0.7 * sunAmt);
    col = mix(col, mc, mist * 0.38);
  }

  // sun bloom with slow shimmer
  vec2 img = uCenter + (p - 0.5) * uVis;
  vec2 sd = (img - uSunUv) * vec2(${(1264 / 848).toFixed(4)}, 1.0);
  float r = length(sd);
  float glow = exp(-r * 9.0) * 0.55 + exp(-r * 32.0) * 0.6;
  glow *= 0.92 + 0.08 * sin(uTime * 1.3);
  col += vec3(1.0, 0.62, 0.28) * glow * 0.5;

  // vignette + grain
  vec2 vv = vUv - 0.5;
  col *= 1.0 - dot(vv, vv) * 0.55;
  col += (hash(vUv * uRes + fract(uTime * 7.0) * 100.0) - 0.5) * 0.025 * uFade;
  gl_FragColor = vec4(col, uFade);
}`;

export function createBackdrop(texture) {
  const material = new THREE.ShaderMaterial({
    vertexShader: "varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }",
    fragmentShader: frag,
    uniforms: {
      uTex: { value: texture },
      uRes: { value: new THREE.Vector2(1, 1) },
      uCenter: { value: new THREE.Vector2(0.5, 0.5) },
      uVis: { value: new THREE.Vector2(1, 1) },
      uShift: { value: new THREE.Vector2() },
      uRoll: { value: 0 },
      uVP: { value: new THREE.Vector2(-1, 0.6) },
      uHorizon: { value: 0.6 },
      uPhase: { value: 0 },
      uFlowK: { value: 0.055 },
      uCamR: { value: new THREE.Vector3(1, 0, 0) },
      uCamU: { value: new THREE.Vector3(0, 1, 0) },
      uCamF: { value: new THREE.Vector3(0, 0, -1) },
      uTan: { value: new THREE.Vector2(1, 1) },
      uTravel: { value: new THREE.Vector2() },
      uMistH: { value: 3.4 },
      uSunW: { value: new THREE.Vector3(0.75, 0.1, -1) },
      uSunUv: { value: new THREE.Vector2(...PLATE.sun) },
      uTime: { value: 0 },
      uFade: { value: 0 },
    },
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  return { mesh, material };
}

// Fullscreen fbm cloud veil used to hide the space scene behind weather.
const veilFrag = /* glsl */ `
precision highp float;
uniform float uTime;
uniform float uK;
uniform float uWarm;
varying vec2 vUv;
${noiseGLSL}
void main() {
  vec2 p = vUv * vec2(2.4, 1.7);
  float n = fbm(p + vec2(uTime * 0.016, -uTime * 0.011));
  float n2 = fbm(p * 2.3 + vec2(-uTime * 0.028, uTime * 0.018) + n * 1.4);
  float cover = smoothstep(0.30, 0.78, n * 0.62 + n2 * 0.55);
  float a = uK * (0.74 + 0.26 * cover);
  vec3 cool = mix(vec3(0.38, 0.44, 0.55), vec3(0.97, 0.985, 1.0), cover * 0.9 + 0.1);
  vec3 warm = mix(vec3(0.30, 0.26, 0.29), vec3(1.0, 0.83, 0.64), cover * 0.9 + 0.1);
  vec3 col = mix(cool, warm, uWarm);
  col *= 0.82 + 0.18 * smoothstep(0.9, 0.3, vUv.y);
  gl_FragColor = vec4(col, a);
}`;

export function createVeil() {
  const material = new THREE.ShaderMaterial({
    vertexShader: "varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }",
    fragmentShader: veilFrag,
    uniforms: {
      uTime: { value: 0 },
      uK: { value: 0 },
      uWarm: { value: 0 },
    },
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 4;
  return { mesh, material };
}

// cover-fit of the plate; returns image-space center + visible span and plate horizon in screen uv
export function coverFit(aspect) {
  const ia = PLATE.aspect;
  const s = aspect > ia ? [1, ia / aspect] : [aspect / ia, 1];
  const vis = [s[0] / OVERSCAN, s[1] / OVERSCAN];
  const want = aspect < 1 ? [0.64, 0.5] : [0.5, 0.46];
  const c = want.map((v, i) => Math.min(Math.max(v, vis[i] / 2 + 0.03), 1 - vis[i] / 2 - 0.03));
  const horizon = 0.5 + (PLATE.horizon - c[1]) / vis[1];
  return { center: c, vis, horizon };
}
